/**
 * The agent role a `cp.ProvisionContractAgent` row carries — the cp-local SUPERSET of the cm role
 * enum (02-datenmodell.md §5 R-08, requirement §3.2).
 *
 * cp needs one role value that cm does not have and must never have: {@link calculated}, the role
 * of a row a `cp.ProvisionTypeCalculatedAgent` rule derived from the contract dimensions and the
 * already resolved trigger agent, rather than from a `cm.ContractAgent` link. Widening the cm enum
 * would push a purely commission-side concept into the contract module, so the enum is copied into
 * cp and extended here.
 *
 * ORDINAL ALIGNMENT CONTRACT — DO NOT REORDER, DO NOT INSERT. The first three constants are
 * ordinal-identical to `ContractAgentEnum` (main = 0, support = 1, inquired = 2), and
 * {@link calculated} = 3 is appended after them:
 *
 *   ProvisionAgentRoleEnum.main       (0) === ContractAgentEnum.main     (0)
 *   ProvisionAgentRoleEnum.support    (1) === ContractAgentEnum.support  (1)
 *   ProvisionAgentRoleEnum.inquired   (2) === ContractAgentEnum.inquired (2)
 *   ProvisionAgentRoleEnum.calculated (3) --- no counterpart in cm
 *
 * That alignment is the entire reason the backend type change of `agent_role` needed no data
 * migration: every ordinal already stored keeps its meaning. Reordering these constants or
 * inserting a value before {@link calculated} silently re-maps every persisted row.
 *
 * Position-based mirror of the backend enum {@code com.mvs.backend.cp.enums.ProvisionAgentRoleEnum}.
 * The backend persists the ordinal (`@Enumerated(EnumType.ORDINAL)`), so the declaration order
 * below MUST match the backend exactly and new constants MUST only be appended at the end — never
 * inserted between existing ones, which would silently re-map every stored value
 * (02-datenmodell.md §0.5/§2).
 *
 * The German labels ("abgeschlossen durch", "unterstützt durch", "eingeholt durch", "berechnet")
 * live on the backend as `@UiEnumValueInfo(uiLabel = ...)` and reach the UI through the FormObject
 * and QL metadata — surfaces rendering this field pick up {@link calculated} automatically and
 * must NOT carry a hardcoded label map.
 *
 * `cp.ProvisionTypeComponent.agentRole` deliberately keeps the cm `ContractAgentEnum` and stays
 * three-valued: a commission component can never PAY the calculated role, which is what forbids a
 * calculated row from triggering further calculated rows.
 */
export enum ProvisionAgentRoleEnum {

    /** Ordinal 0 — "abgeschlossen durch". The agent who closed the contract. */
    main,

    /** Ordinal 1 — "unterstützt durch". A supporting agent; several per contract are legal. */
    support,

    /** Ordinal 2 — "eingeholt durch". The agent the contract was procured through. */
    inquired,

    /**
     * Ordinal 3 — "berechnet". The cp-only role: a row no contract agent link produced, but a
     * `cp.ProvisionTypeCalculatedAgent` rule derived. Has NO counterpart in cm and is therefore
     * structurally impossible as the TRIGGER of a rule (recursion ban, FD-2).
     */
    calculated,
}
